import React, { useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import axios from 'axios';

export default function TagsModel({ open, onClose, eventId, roleId, tagId }) {
    const apiUrl = process.env.REACT_APP_API_BASE_URL;
    const bearerToken = localStorage.getItem('token');
    const [isLoading, setIsLoading] = useState(false);
    const modalRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (modalRef.current && !modalRef.current.contains(event.target)) {
                onClose();
            }
        };

        if (open) {
            document.addEventListener("mousedown", handleClickOutside);
        }

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [open, onClose]);

    const handleClose = () => {
        onClose();
        newTag.resetForm();
    };

    const addTag = async (values) => {
        setIsLoading(true);
        try {
            const formData = new URLSearchParams();
            formData.append("name", values.name);
            formData.append("roleId", roleId);
            formData.append("eventId", eventId);

            let response;

            if (tagId) {
                response = await axios.put(
                    `${apiUrl}event-service/event-tag/${tagId}`,
                    formData,
                    {
                        headers: {
                            Authorization: `Bearer ${bearerToken}`,
                            "Content-Type": "application/x-www-form-urlencoded",
                            accept: "application/json",
                        },
                    }
                );
            } else {
                response = await axios.post(
                    `${apiUrl}event-service/event-tag`,
                    formData,
                    {
                        headers: {
                            Authorization: `Bearer ${bearerToken}`,
                            "Content-Type": "application/x-www-form-urlencoded",
                            accept: "application/json",
                        },
                    }
                );
            }

            if (response.data?.success) {
                setIsLoading(false);
                handleClose();
            } else {
                throw new Error(tagId ? "Failed to update tag" : "Failed to create tag");
            }
        } catch (error) {
            console.error("Error saving tag:", error.message);
        } finally {
            setIsLoading(false);
            handleClose();
        }
    };

    const newTag = useFormik({
        initialValues: {
            name: '',
            roleId: roleId || '',
            eventId: eventId || '',
        },
        validationSchema: Yup.object({
            name: Yup.string().required('Required'),
        }),
        onSubmit: async (values) => {
            try {
                await addTag(values);
            } catch (error) {
                console.error('Error submitting form:', error.message);
            }
        }
    });

    const fetchTags = async () => {
        if (!tagId) return;

        setIsLoading(true);
        try {
            const response = await axios.get(`${apiUrl}event-service/event-tag/${tagId}`, {
                headers: {
                    'Authorization': `Bearer ${bearerToken}`,
                    'accept': 'application/json',
                },
            });

            if (response.data?.success) {
                const tagData = response.data.data;
                newTag.setValues({
                    name: tagData.name || '',
                    roleId: roleId || '',
                    eventId: eventId || '',
                });
            } else {
                throw new Error('Failed to fetch tag data');
            }
        } catch (error) {
            console.error('Error fetching tag data:', error.message);
        } finally {
            setIsLoading(false);
        }
    }

    useEffect(() => {
        if (open && tagId)
            fetchTags();
    }, [open, tagId]);

    if (!open) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            {/* Background overlay */}
            <div className="absolute inset-0 bg-black opacity-50" onClick={onClose}></div>

            {/* Modal */}
            <div className="bg-white rounded-lg w-full max-w-xl z-50 relative">
                {/* Header */}
                <div className="bg-blue-500 text-white py-3 px-6 rounded-lg w-3/4 mx-auto relative z-10 -mt-6">
                    <h2 className="text-xl font-semibold text-center">{tagId ? 'Edit Tag' : 'Add New Tag'}</h2>
                </div>

                <div className="relative bg-white p-6 space-y-6 shadow-md rounded-b-lg">
                    <form onSubmit={newTag.handleSubmit} className="space-y-6">
                        {/* Name and Status Row */}
                        <div className="flex space-x-4">
                            <div className="flex-1">
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Name <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="text"
                                    name="name"
                                    placeholder="name of the tag"
                                    value={newTag.values.name}
                                    onChange={newTag.handleChange}
                                    onBlur={newTag.handleBlur}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                />
                                {newTag.touched.name && newTag.errors.name && (
                                    <div className="text-red-500 text-xs mt-1">{newTag.errors.name}</div>
                                )}
                            </div>
                        </div>
                        <div className='flex justify-end'>
                            <button
                                type="submit"
                                disabled={isLoading}
                                className="bg-blue-500 text-white px-4 py-2 rounded-md hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500"
                            >
                                {tagId
                                    ? (isLoading ? "Updating..." : "Update")
                                    : (isLoading ? "Saving..." : "Save")
                                }
                            </button>
                        </div>
                    </form>
                </div>
            </div>

        </div>
    );
}

TagsModel.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    eventId: PropTypes.string.isRequired,
    roleId: PropTypes.string.isRequired,
    tagId: PropTypes.string,
};